/**
 * download — turn a fetched Blob into a file the browser saves.
 *
 * The anchor-click dance was hand-rolled in eight places (the CSV util, the
 * admin audit export, the attachment viewer, the Google Drive context, …),
 * each with its own idea of whether to revoke the object URL afterwards.
 * Every new binary download should use this instead.
 *
 * `filenameFromContentDisposition` is here for the same reason: the header is
 * the server's own answer to what the file is called, and parsing it is
 * fiddly enough (quoting, and the RFC 5987 `filename*` form) that nobody
 * should do it inline.
 */

/**
 * Save a Blob under `filename`.
 *
 * The object URL is revoked on the next tick rather than immediately: Safari
 * has been known to cancel a download whose URL is revoked in the same task
 * as the click, and a leaked URL for one frame is the cheaper mistake.
 */
export function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

/**
 * The filename a `Content-Disposition` header asks for, or `null`.
 *
 * Handles the three forms that turn up in practice:
 *
 *   attachment; filename="matter export.zip"
 *   attachment; filename=matter.zip
 *   attachment; filename*=UTF-8''matter%20export.zip
 *
 * `filename*` WINS when both are present — that is what RFC 6266 says, and it
 * is the one that carries non-ASCII characters, so a matter named in Bulgarian
 * or Greek keeps its name instead of falling back to the ASCII-mangled twin
 * the server sent alongside it.
 */
export function filenameFromContentDisposition(header?: string | null): string | null {
  if (!header) return null;

  const extended = /filename\*\s*=\s*([^']*)'([^']*)'([^;]+)/i.exec(header);
  if (extended) {
    try {
      return sanitiseFilename(decodeURIComponent(extended[3].trim()));
    } catch {
      /* a malformed percent-escape falls through to the plain form */
    }
  }

  const quoted = /filename\s*=\s*"([^"]*)"/i.exec(header);
  if (quoted) return sanitiseFilename(quoted[1]);

  const bare = /filename\s*=\s*([^;]+)/i.exec(header);
  if (bare) return sanitiseFilename(bare[1].trim());

  return null;
}

/**
 * Strip anything that would make the name a path.
 *
 * The header is server-controlled, but `download` treats a name with slashes
 * inconsistently across browsers and a `../` in it has no business reaching
 * the filesystem. Only the last segment is kept.
 */
function sanitiseFilename(raw: string): string {
  const name = raw.split(/[\\/]/).pop()?.trim() || '';
  // Control characters, plus the set Windows refuses in a filename.
  // eslint-disable-next-line no-control-regex
  return name.replace(/[\x00-\x1f<>:"|?*]/g, '').trim();
}

/** Force an extension when the server did not give us a usable name. */
export function withExtension(name: string, extension: string): string {
  const clean = sanitiseFilename(name) || 'download';
  const suffix = extension.startsWith('.') ? extension : `.${extension}`;
  return clean.toLowerCase().endsWith(suffix.toLowerCase()) ? clean : `${clean}${suffix}`;
}
